import React, { useEffect, useState } from "react";
import { X, Clock, MapPin, CalendarDays } from "lucide-react";
import api from "../api";
import Portal from "./common/Portal";

// Read-only view of a doctor's weekly schedule (the same weekly_schedule the
// edit form's WeeklyScheduleEditor writes), so staff without edit rights -
// e.g. a receptionist - can check hours, locations and fees quickly.
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${suffix}`;
}

export default function DoctorScheduleModal({ doctor, onClose }) {
  const [branches, setBranches] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const schedule = doctor?.weekly_schedule || {};
  const todayKey = DAYS[(new Date().getDay() + 6) % 7];

  useEffect(() => {
    let alive = true;
    api.fetchOrgBranches(true)
      .then((res) => { if (alive && Array.isArray(res.data)) setBranches(res.data); })
      .catch(() => {});
    api.getGeneralSettings()
      .then((res) => { if (alive && res.data) setBookingSettings(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const branchName = (id) => {
    if (id == null) return branches.length > 0 ? "Org default address" : null;
    const b = branches.find((x) => x.id === id);
    return b ? `${b.name}${b.is_active ? "" : " (archived)"}` : "Unknown location";
  };

  // Same formula as WeeklyScheduleEditor's Pay at Clinic preview.
  const unpaidFee = (fee) => {
    if (!bookingSettings || bookingSettings.allow_unpaid_booking === false) return null;
    const diff = parseFloat(bookingSettings.unpaid_fee_difference_value) || 0;
    if (!diff) return null;
    const base = parseFloat(fee) || 0;
    return Math.round(bookingSettings.unpaid_fee_difference_type === "percent" ? base + (base * diff) / 100 : base + diff);
  };

  const workingDays = DAYS.filter((d) => schedule[d]?.is_working && (schedule[d]?.shifts || []).length > 0);

  return (
    <Portal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-5 py-4 border-b border-secondary-100">
            <div>
              <h3 className="text-base font-bold text-secondary-900">{doctor.name}</h3>
              <p className="text-xs text-secondary-500 mt-0.5">
                Weekly schedule
                {doctor.specialized ? ` · ${doctor.specialized}` : ""}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-500" title="Close">
              <X size={18} />
            </button>
          </div>

          <div className="px-5 py-3 flex flex-wrap gap-2 text-xs border-b border-secondary-100">
            <span className="inline-flex items-center gap-1 bg-secondary-50 text-secondary-700 px-2 py-1 rounded-md">
              <Clock size={13} /> {schedule.slot_duration || 30} min slots
            </span>
            {schedule.buffer_minutes > 0 && (
              <span className="bg-secondary-50 text-secondary-700 px-2 py-1 rounded-md">
                {schedule.buffer_minutes} min gap after each
              </span>
            )}
            <span className="inline-flex items-center gap-1 bg-secondary-50 text-secondary-700 px-2 py-1 rounded-md">
              <CalendarDays size={13} /> {workingDays.length} working day{workingDays.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="overflow-y-auto p-5 space-y-2">
            {workingDays.length === 0 && (
              <p className="text-sm text-secondary-500 text-center py-6">No availability set up yet.</p>
            )}
            {workingDays.length > 0 && DAYS.map((day) => {
              const cfg = schedule[day];
              const working = cfg?.is_working && (cfg?.shifts || []).length > 0;
              const isToday = day === todayKey;
              return (
                <div
                  key={day}
                  className={`flex gap-3 rounded-xl border px-3 py-2.5 ${
                    isToday ? "border-primary-300 bg-primary-50/40" : working ? "border-secondary-200" : "border-secondary-100 bg-secondary-50/50"
                  }`}
                >
                  <div className="w-24 shrink-0 pt-0.5">
                    <span className={`text-sm font-semibold capitalize ${working ? "text-secondary-900" : "text-secondary-400"}`}>{day}</span>
                    {isToday && <span className="block text-[10px] font-semibold text-primary-600">Today</span>}
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {!working && <span className="text-sm text-secondary-400 italic">Off</span>}
                    {working && cfg.shifts.map((s, i) => {
                      const loc = branchName(s.branch_id);
                      const clinic = unpaidFee(s.fee);
                      return (
                        <div key={i} className="text-sm">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className="font-medium text-secondary-900">{formatTime(s.start)} – {formatTime(s.end)}</span>
                            {s.fee != null && (
                              <span className="text-secondary-700">
                                ₹{s.fee}
                                {clinic != null && <span className="text-xs text-amber-700"> (₹{clinic} at clinic)</span>}
                              </span>
                            )}
                          </div>
                          {loc && (
                            <div className="flex items-center gap-1 text-xs text-secondary-500">
                              <MapPin size={12} /> {loc}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Portal>
  );
}
